/**
 * The mechanism's one function (ADR-119 decision 2, "One function" and "A missing key"). `say` words a key from a
 * catalogue, falling back to the bundled English for a key the catalogue lacks, for a value whose placeholders
 * differ from the English one's and for a plural object without `other`. It is pure: the locale is the catalogue's,
 * a time zone is the parameter's, and nothing reads the clock or the runtime's locale. It never throws and never
 * shows a key: a key neither catalogue words gives the empty string, which the caller replaces with its own
 * fallback sentence.
 */

import { formatNumber, formatParam, isKeyParam, pluralCategory } from "./format.ts";
import { parseTemplate } from "./placeholders.ts";
import type { Params, SayParams, WordKey, WordValueAt } from "./types.ts";
import { fitsFallback, isUsable, wordAt } from "./values.ts";
import type { Catalogue, WordValue } from "./values.ts";

/**
 * The value `say` shows for a key and the locale it is formatted in.
 */
interface Chosen {
  readonly value: WordValue;
  readonly locale: string;
}

/**
 * Chooses the catalogue's value when it fits the fallback's, and otherwise the fallback's when it can be shown.
 *
 * @param catalogue The active catalogue.
 * @param fallback The bundled English.
 * @param key The key.
 * @returns The value and its locale, or null when neither catalogue words the key.
 */
function choose(catalogue: Catalogue, fallback: Catalogue, key: string): Chosen | null {
  const english = wordAt(fallback.words, key);
  if (english === undefined || !isUsable(english)) {
    return null;
  }
  const own = catalogue === fallback ? undefined : wordAt(catalogue.words, key);
  if (own !== undefined && fitsFallback(own, english)) {
    return { value: own, locale: catalogue.locale };
  }
  return { value: english, locale: fallback.locale };
}

/**
 * Words a nested key: the chosen value, which must be a string with no placeholders.
 *
 * @param catalogue The active catalogue.
 * @param fallback The bundled English.
 * @param key The nested key.
 * @returns Its text, or the empty string when it is not plain text in either catalogue.
 */
function nested(catalogue: Catalogue, fallback: Catalogue, key: string): string {
  const chosen = choose(catalogue, fallback, key);
  if (chosen === null || typeof chosen.value !== "string") {
    return "";
  }
  const parsed = parseTemplate(chosen.value);
  if (!parsed.ok) {
    return "";
  }
  let text = "";
  for (const part of parsed.parts) {
    if ("name" in part) {
      return "";
    }
    text += part.text;
  }
  return text;
}

/**
 * Words a key.
 *
 * @param catalogue The active catalogue: a translation, a published wording file, or the English itself.
 * @param fallback The bundled English, whose `const` type gives the keys and their parameters.
 * @param key The key.
 * @param params The value's parameters, exactly: one per placeholder, and `count` for a plural object. Omitted when
 * the value takes none.
 * @returns The words, or the empty string when neither catalogue words the key.
 */
export function say<C extends Catalogue, K extends WordKey<C>, const P extends Params<WordValueAt<C, K>> = Params<WordValueAt<C, K>>>(catalogue: Catalogue, fallback: C, key: K, ...params: SayParams<WordValueAt<C, K>, P>): string {
  const chosen = choose(catalogue, fallback, key);
  if (chosen === null) {
    return "";
  }
  const given: { readonly [name: string]: unknown } = params[0] ?? {};
  const count = Object.hasOwn(given, "count") ? given["count"] : undefined;
  const text = typeof chosen.value === "string" ? chosen.value : (chosen.value[pluralCategory(chosen.locale, count)] ?? chosen.value.other);
  if (text === undefined) {
    return "";
  }
  const parsed = parseTemplate(text);
  if (!parsed.ok) {
    return "";
  }
  let words = "";
  for (const part of parsed.parts) {
    if ("text" in part) {
      words += part.text;
      continue;
    }
    const value = Object.hasOwn(given, part.name) ? given[part.name] : undefined;
    if (isKeyParam(value)) {
      words += nested(catalogue, fallback, value.k);
    } else if (part.name === "count" && typeof value === "number") {
      words += formatNumber(chosen.locale, value);
    } else {
      words += formatParam(chosen.locale, value);
    }
  }
  return words;
}
